import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, PageHeader, Section, EmptyState } from "@/components/Primitives";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, LineChart } from "recharts";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [tests, setTests] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/analytics/student/${user.id}`).then((r) => setAnalytics(r.data));
    api.get("/tests/available").then((r) => setTests(r.data));
    api.get("/notifications").then((r) => setNotifs(r.data));
  }, [user.id]);

  const pending = tests.filter(t => !t.attempted).length;
  const unread = notifs.filter(n => !n.read).length;
  const avg = analytics && analytics.by_subject.length
    ? (analytics.by_subject.reduce((s, x) => s + x.percentage, 0) / analytics.by_subject.length).toFixed(1)
    : "—";

  return (
    <div>
      <PageHeader eyebrow={user.class_group || "Student"} title="Your academic snapshot" subtitle="An honest read on progress, strengths and chapters to revisit." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
        <StatCard label="Tests available" value={pending} sub="waiting on you" accent="terracotta" />
        <StatCard label="Tests attempted" value={analytics?.attempts_count ?? 0} />
        <StatCard label="Overall avg." value={`${avg}${analytics ? "%" : ""}`} />
        <StatCard label="Unread alerts" value={unread} />
      </div>

      {analytics && analytics.monthly.length > 0 && (
        <Section title="Monthly performance">
          <div className="card-academic p-6" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.monthly}>
                <CartesianGrid stroke="#D8D3C8" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#526055" tick={{ fontSize: 11 }} />
                <YAxis unit="%" stroke="#526055" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line dataKey="percentage" stroke="#C55743" strokeWidth={2} dot={{ fill: "#1B3C35" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      {analytics && analytics.by_subject.length > 0 && (
        <Section title="Subject-wise average">
          <div className="card-academic p-6" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.by_subject}>
                <CartesianGrid stroke="#D8D3C8" strokeDasharray="3 3" />
                <XAxis dataKey="subject" stroke="#526055" tick={{ fontSize: 11 }} />
                <YAxis unit="%" stroke="#526055" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="percentage" fill="#1B3C35" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      <Section title="Strengths & chapters to revisit">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-academic p-6">
            <div className="overline text-forest mb-3">Strong chapters</div>
            {(!analytics || analytics.strong_chapters.length === 0) ? <div className="text-sm text-mossy">Take more tests to surface strengths.</div> :
              <ul className="space-y-2 text-sm">
                {analytics.strong_chapters.map(c => (
                  <li key={c.chapter} className="flex justify-between"><span>{c.subject} · {c.chapter}</span><span className="font-mono text-forest">{c.percentage}%</span></li>
                ))}
              </ul>
            }
          </div>
          <div className="card-academic p-6">
            <div className="overline text-terracotta mb-3">Weak chapters</div>
            {(!analytics || analytics.weak_chapters.length === 0) ? <div className="text-sm text-mossy">No weak chapters detected yet.</div> :
              <ul className="space-y-2 text-sm">
                {analytics.weak_chapters.map(c => (
                  <li key={c.chapter} className="flex justify-between"><span>{c.subject} · {c.chapter}</span><span className="font-mono text-terracotta">{c.percentage}%</span></li>
                ))}
              </ul>
            }
          </div>
        </div>
      </Section>

      <Section title="Available tests" right={<button onClick={() => nav("/student/tests")} className="btn-ghost text-xs">See all</button>}>
        {tests.length === 0 ? <EmptyState title="No tests available" /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {tests.slice(0, 4).map(t => (
              <div key={t.id} className="card-academic p-5">
                <div className="overline">{t.class_group} · {t.duration_minutes} min</div>
                <h4 className="font-serif text-xl text-forest">{t.title}</h4>
                <div className="mt-3">
                  {t.attempted
                    ? <span className="overline text-forest">Attempted</span>
                    : <button onClick={() => nav(`/student/tests/${t.id}`)} className="btn-primary text-xs px-4 py-2" data-testid={`take-${t.id}`}>Take test</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
